/**
 * A trip's dates, written the one way this app writes them.
 *
 * The trips list has always printed `03/10 – 05/10`, and the trip's own page printed the stored ISO
 * `2026-10-03 → 2026-10-05`, because each screen had its own answer and only one of them had bothered to
 * format. Two screens describing one trip two ways is the kind of difference nobody reports and everybody
 * half-notices, so the list's rule lives here and every screen that shows a date range calls it —
 * the same reason `plan.ts` holds the plan's numbers.
 *
 * Not `toLocaleDateString`: the app has one date format and it is written down. A locale call renders
 * differently on a device set to another region, so two people comparing the same trip over the phone
 * would read different dates off the same row.
 *
 * The year is carried only when the two ends disagree on it, and then on **both** ends: a range stored
 * from 30/12 to 02/01 read `30/12 – 02/01/2027` is worse than either alternative, because the year on one
 * end alone makes the reader do the comparison the format was already doing for them.
 */

import { copy } from "../copy.ts";

/** "03/10", or "03/10/2026" when the trip's two dates do not share a year. */
function shortDate(iso: string, withYear: boolean): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return null;
  const [, year, month, day] = match;
  return withYear ? `${day as string}/${month as string}/${year as string}` : `${day as string}/${month as string}`;
}

/**
 * A trip's two dates as the list and the trip's own page write them, or null for a date the contract's
 * format cannot read.
 *
 * Null rather than a fallback string, so a caller can decide what to do with an unreadable date instead of
 * printing something the user cannot tell apart from a real range.
 */
export function tripDateRange(start: string, end: string): string | null {
  const from = shortDate(start, false);
  const to = shortDate(end, false);
  if (from === null || to === null) return null;

  const withYear = start.slice(0, 4) !== end.slice(0, 4);
  return copy.trips.dateRange(shortDate(start, withYear) ?? from, shortDate(end, withYear) ?? to);
}

/**
 * A single day of a trip as "12/10", or "02/01/2027" for the day the trip crosses into the next year.
 *
 * `dayIndex` is 1-based, matching `PlanDay.day_index`. The year rule is the range's own: it is carried only
 * when the day's year differs from the start's, by the same `shortDate` call that writes a range, so the two
 * surfaces cannot write one trip differently.
 *
 * Null for a date the calendar does not hold rather than a date rolled forward. `"2026-02-31"` and
 * `"2026-13-45"` are not a day later: `new Date(2026, 1, 31)` silently answers 3 March, printing a date the
 * stored record never contained — the one thing a wrong record must not do is quietly become a plausible
 * different one. The existence check round-trips the written parts through the calendar, because the regex
 * already guarantees integers and the old `isNaN` guard could therefore never fire.
 */
export function formatDayDate(startDate?: string | null, dayIndex = 1): string | null {
  if (!startDate) return null;
  const iso = tripDayIso(startDate, dayIndex);
  if (iso === null) return null;
  return shortDate(iso, iso.slice(0, 4) !== startDate.slice(0, 4));
}

/**
 * A date in the contract's format, as UTC midnight, or null when it is not a day the calendar holds.
 *
 * UTC rather than local midnight: this file counts whole days (`86_400_000ms`), and a local `Date` shifts
 * by the timezone's offset. The written parts must survive the round trip, so `"2026-02-31"` is null
 * rather than the 3 March the platform silently answers.
 */
function dayMs(iso: string): number | null {
  if (!/^(\d{4})-(\d{2})-(\d{2})$/.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  const roundTrip = new Date(ms).toISOString().slice(0, 10);
  return roundTrip === iso ? ms : null;
}

/**
 * Today, as the ISO date the contract stores, in the browser's own timezone.
 *
 * The local calendar fields rather than `toISOString()`: that formats the instant in UTC, so a user east
 * of UTC after their own midnight — Vietnam at 00:30 — would be handed yesterday for the next seven
 * hours. The one place this app writes "today", so no second copy can answer differently.
 */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/**
 * The ISO date of a trip's day: day 1 is the start date, day 2 the next, and so on. Null when the start
 * date or the index names no day — a zero, negative or fractional index, or one so large the arithmetic
 * leaves the calendar. The inverse of `formatDayDate`, for a control that edits a date rather than prints
 * one.
 */
export function tripDayIso(startDate: string, dayIndex: number): string | null {
  if (!Number.isInteger(dayIndex) || dayIndex < 1) return null;
  const start = dayMs(startDate);
  if (start === null) return null;
  const at = new Date(start + (dayIndex - 1) * 86_400_000);
  if (Number.isNaN(at.getTime())) return null;
  return at.toISOString().slice(0, 10);
}

/**
 * Which day of the trip a date is, 1-based, or null when it falls before the start date (or either string
 * is not a date). A date after the trip's end still answers with the day it would be: this file knows the
 * start and not the range's other end, so bounding the answer is the caller's.
 */
export function tripDayIndex(startDate: string, iso: string): number | null {
  const start = dayMs(startDate);
  const day = dayMs(iso);
  if (start === null || day === null) return null;
  const index = Math.round((day - start) / 86_400_000) + 1;
  return index < 1 ? null : index;
}

